import IIntegratorRunLog from "../interfaces/models/IIntegratorRunLog";
import Axios from "../utils/axios";

const axios = new Axios();

/**
 * Runs the Integrator import for one firm and returns its execution log.
 *
 * A run that *failed* still answers 200 with the log — the outcome is in `status` and
 * `errorMessage`, not in the status code. Callers must read the returned document instead
 * of assuming a resolved promise means the feed was stored.
 *
 * Rejects only when the run could not be recorded at all: firm not configured (404),
 * disabled (400), or a run already in flight (409).
 */
export interface IntegratorRunLogQuery {
  firmId?: string;
  /** Every log of the same run, across firms. */
  runId?: string;
  pageIndex?: number;
  pageSize?: number;
}

/**
 * Reads the execution log, newest first.
 *
 * Filtering by `runId` alone reads across firms — it is what shows a run as a unit. Both
 * filters can be combined, and omitting them returns the latest runs of every firm.
 */
export const getIntegratorRunLogs = async (
  query: IntegratorRunLogQuery = {}
): Promise<IIntegratorRunLog[]> => {
  try {
    const params = new URLSearchParams();
    if (query.firmId) {
      params.append("firmId", query.firmId);
    }
    if (query.runId) {
      params.append("runId", query.runId);
    }
    if (query.pageSize) {
      params.append(
        "pagination",
        JSON.stringify({ pageIndex: query.pageIndex ?? 0, pageSize: query.pageSize })
      );
    }

    const queryString = params.toString();
    const response = await axios.Get(
      queryString ? `/integratorRunLog?${queryString}` : "/integratorRunLog"
    );
    return response.data ?? [];
  } catch (error) {
    console.error("Error fetching integrator run logs:", error);
    return [];
  }
};

/**
 * The most recent log of each firm. Firms with no history are omitted; join by `firmId`.
 *
 * This is a different question from `getIntegratorRunLogs`: that one returns history
 * (paginated). Sending the same `firmId IN (...)` filter there would mix every run of
 * those firms and clip the page, not one row each.
 */
export const getLatestIntegratorRunLogs = async (
  firmIds: string[]
): Promise<IIntegratorRunLog[]> => {
  const params = new URLSearchParams();
  params.append("firmIds", firmIds.join(","));
  const response = await axios.Get(`/integratorRunLog/latest?${params.toString()}`);
  return response.data ?? [];
};

/**
 * One log by document id. Cross-partition: the list GET's partition key is `firmId`,
 * and a deep link only has `id`. Acceptable because it answers a single document.
 */
export const getIntegratorRunLogById = async (
  id: string
): Promise<IIntegratorRunLog | undefined> => {
  const params = new URLSearchParams();
  params.append(
    "filter",
    JSON.stringify({ id: { value: id, comparisonType: "EQUAL" } })
  );
  const response = await axios.Get(`/integratorRunLog?${params.toString()}`);
  const logs = response.data ?? [];
  return logs[0];
};

export interface IntegratorRunLogAssetLink {
  fileName: string;
  url: string;
}

export interface IntegratorRunLogAssets {
  blobPath: IntegratorRunLogAssetLink | null;
  jsonBlobPath: IntegratorRunLogAssetLink | null;
  resultBlobPath: IntegratorRunLogAssetLink | null;
}

export interface IntegratorRunLogAssetsResult {
  expiresAt: number;
  assets: IntegratorRunLogAssets;
}

/**
 * Short-lived read URLs for the three files a run may have stored.
 *
 * A `null` entry means that field is empty on the log (a skipped or failed run
 * often has no result file). The browser downloads from Storage; this call
 * only signs.
 */
export const getIntegratorRunLogAssets = async (query: {
  id: string;
  firmId: string;
}): Promise<IntegratorRunLogAssetsResult> => {
  const params = new URLSearchParams();
  params.append("firmId", query.firmId);
  const response = await axios.Get(
    `/integratorRunLog/${query.id}/assets?${params.toString()}`
  );
  return response.data;
};

/**
 * What a run answers with. The log is nested rather than returned on its own because the
 * run also reports whether its result was mailed — and only the log travels back: what the
 * integrators did grows with the feed, so it goes out in the report instead.
 */
export interface IntegratorRunResult {
  runLog: IIntegratorRunLog;
  /** Whether the report reached the reviewers. A false here does not mean the run failed. */
  emailSent: boolean;
}

export const runIntegratorImport = async (
  firmId: string,
  options: { force?: boolean; feedData?: string; fileName?: string } = {}
): Promise<IntegratorRunResult> => {
  try {
    const response = await axios.Post("/integratorRunLog", {
      firmId,
      force: options.force === true,
      ...(options.feedData
        ? { feedData: options.feedData, fileName: options.fileName }
        : {}),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(integratorRunLogError(error, "Could not run the import."));
  }
};

/**
 * Starts an existing queued log in this request. Does not open a new log.
 */
export const startQueuedIntegratorImport = async (query: {
  id: string;
  firmId: string;
}): Promise<IntegratorRunResult> => {
  try {
    const response = await axios.Put("/integratorRunLog", query);
    return response.data;
  } catch (error: any) {
    throw new Error(integratorRunLogError(error, "Could not start this run."));
  }
};

/**
 * Deletes this log regardless of status. Stored blobs are left in place.
 */
export const setIntegratorRunLogStaffReview = async (query: {
  id: string;
  firmId: string;
  reviewed: boolean;
}): Promise<IIntegratorRunLog> => {
  try {
    const params = new URLSearchParams();
    params.append("firmId", query.firmId);
    const response = await axios.PutDataIntoModel(
      `/integratorRunLog/${query.id}/staffReview?${params.toString()}`,
      { reviewed: query.reviewed }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      integratorRunLogError(error, "Could not update staff review.")
    );
  }
};

export const deleteIntegratorRunLog = async (query: {
  id: string;
  firmId: string;
}): Promise<void> => {
  try {
    // Axios.Delete only appends `id`; firmId is the partition key.
    await axios.Delete(
      "/integratorRunLog",
      `${query.id}&firmId=${encodeURIComponent(query.firmId)}`
    );
  } catch (error: any) {
    throw new Error(integratorRunLogError(error, "Could not delete this run log."));
  }
};

function integratorRunLogError(error: any, fallback: string): string {
  const body = error?.response?.data;
  return (
    (typeof body === "string" && body.trim()) ||
    body?.message ||
    error?.message ||
    fallback
  );
}

export interface IntegratorBatchQueueFailure {
  firmId: string;
  errorMessage: string;
  statusCode?: number;
}

/** What `POST /integrator/batch` answers with. The imports have not run yet. */
export interface IntegratorBatchResult {
  runId: string;
  jobs: IIntegratorRunLog[];
  failures: IntegratorBatchQueueFailure[];
}

/**
 * Reserves one queued log per enabled firm and enqueues each job.
 *
 * Answers 202: the logs exist, the imports run later. Rejects when the batch
 * could not be recorded (Service Bus send failure, etc.).
 */
export const runIntegratorBatch = async (): Promise<IntegratorBatchResult> => {
  try {
    const response = await axios.Post("/integrator/batch", {});
    return response.data;
  } catch (error: any) {
    const body = error?.response?.data;
    const message =
      (typeof body === "string" && body.trim()) ||
      body?.message ||
      error?.message ||
      "Could not queue the batch.";
    throw new Error(message);
  }
};
